import React from 'react';
import './HardwareHealthTracker.css';

/**
 * Hardware Health Tracker Component
 * 
 * Displays unified hardware health status for core sensors based on canonical
 * names from device firmware configuration (HardwareConfig.json):
 * - proximity: APDS9960 sensor
 * - motor: L298N_Motor 
 * - display: Sharp_MemoryLCD
 * - memory: SystemMemory (CPU)
 * 
 * Shows "Hardware Healthy" with wrench icon if no errors/warnings,
 * otherwise displays specific error/warning text with appropriate styling.
 */
const HardwareHealthTracker = ({ block, compact = false, className = '' }) => {
  
  // Extract core hardware health from webhook errors/warnings
  // Only check the 4 core hardware components: proximity, motor, display, memory
  // "No news is good news" - default to healthy unless explicit errors/warnings
  const getHardwareHealth = () => {
    const coreHardwareErrors = [];
    const coreHardwareWarnings = [];
    
    // Check for proximity sensor errors/warnings from webhooks
    if (block.proximity_error) {
      coreHardwareErrors.push(`Proximity: ${block.proximity_error}`);
    }
    if (block.proximity_warning) {
      coreHardwareWarnings.push(`Proximity: ${block.proximity_warning}`);
    }
    
    // Check for motor hardware errors/warnings from webhooks
    if (block.motor_error) {
      coreHardwareErrors.push(`Motor: ${block.motor_error}`);
    }
    if (block.motor_warning) {
      coreHardwareWarnings.push(`Motor: ${block.motor_warning}`);
    }
    
    // Check for display hardware errors/warnings from webhooks
    if (block.display_error) {
      coreHardwareErrors.push(`Display: ${block.display_error}`);
    }
    if (block.display_warning) {
      coreHardwareWarnings.push(`Display: ${block.display_warning}`);
    }
    
    // Check for memory/CPU errors/warnings from webhooks
    if (block.memory_error) {
      coreHardwareErrors.push(`Memory: ${block.memory_error}`);
    }
    if (block.memory_warning) {
      coreHardwareWarnings.push(`Memory: ${block.memory_warning}`);
    }
    
    return {
      errors: coreHardwareErrors,
      warnings: coreHardwareWarnings,
      isHealthy: coreHardwareErrors.length === 0 && coreHardwareWarnings.length === 0
    };
  };

  const health = getHardwareHealth();
  
  if (compact) {
    // Compact view for card display - no icon since parent stat-item has one
    return (
      <div className={`hardware-health-compact ${className}`}>
        {health.isHealthy ? (
          <span className="health-text healthy">Hardware Healthy</span>
        ) : health.errors.length > 0 ? (
          <span className="health-text error">Hardware Error</span>
        ) : (
          <span className="health-text issues">Hardware Warning</span>
        )}
      </div>
    );
  }

  // Full detailed view
  return (
    <div className={`hardware-health-tracker ${className}`}>
      <div className="health-header">
        <span className="health-icon">
          {health.isHealthy ? '🔧' : (health.errors.length > 0 ? '❌' : '⚠️')}
        </span>
        <span className="health-title">Hardware Health</span>
      </div>
      
      {health.isHealthy ? (
        <div className="health-status healthy">
          <span className="status-text">Hardware Healthy</span>
          <p className="status-description">
            All core hardware sensors are functioning normally.
          </p>
        </div>
      ) : (
        <div className="health-status issues">
          {health.errors.length > 0 && (
            <div className="health-section errors">
              <h4 className="section-title error-title">Hardware Errors</h4>
              <ul className="issue-list">
                {health.errors.map((error, index) => (
                  <li key={`error-${index}`} className="issue-item error">
                    <span className="issue-icon">❌</span>
                    <span className="issue-text">{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {health.warnings.length > 0 && (
            <div className="health-section warnings">
              <h4 className="section-title warning-title">Hardware Warnings</h4>
              <ul className="issue-list">
                {health.warnings.map((warning, index) => (
                  <li key={`warning-${index}`} className="issue-item warning">
                    <span className="issue-icon">⚠️</span>
                    <span className="issue-text">{warning}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      <div className="health-footer">
        <small className="health-note">
          Monitoring: Proximity • Motor • Display • Memory (CPU)
        </small>
      </div>
    </div>
  );
};

export default HardwareHealthTracker;
